/**
 * schemas/config/options.ts
 * ──────────────────────────────────────────────────────────────────────────
 * The five configurable option lists. These are REFERENCE DATA, not enums:
 * an org adds its own holder types and instruments, and a removed value stays
 * on the records that already carry it (invariant 6 — deactivate, never
 * delete). It only leaves the picker.
 *
 * `scope` is deliberately NOT here. It is a fixed enum of three values because
 * each one changes behaviour, and a configurable scope would mean a value the
 * engine has no rules for.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { z } from 'zod';

/** The API's list_kind values — singular, exactly as sent. */
export const LIST_KINDS = ['holderType', 'instrument', 'funding', 'currency', 'approver'] as const;
export type ListKind = (typeof LIST_KINDS)[number];

/** The label and helper note each list shows in Settings. */
export const LIST_META: Record<ListKind, { label: string; note: string }> = {
  holderType: { label: 'Holder types', note: 'Who can be given custody of company money.' },
  instrument: { label: 'Instruments', note: 'What the money physically is.' },
  funding: { label: 'Funding sources', note: 'Where the money came from.' },
  currency: { label: 'Currencies', note: 'Balances are never converted between these.' },
  approver: { label: 'Approvers', note: 'Names an approval rule can route to.' },
};

export const optionSchema = z.object({
  id: z.string(),
  list_kind: z.string(),
  /** Stored slug — `petty_cash`. Currencies are uppercase, approvers as typed. */
  value: z.string(),
  display_name: z.string(),
  /** Currency rows carry their decimal scale; other lists send null. */
  minor_units: z.number().nullable(),
  is_active: z.boolean(),
  sort_order: z.number(),
});
export type OptionRow = z.infer<typeof optionSchema>;

export const optionsResponseSchema = z.object({ options: z.array(optionSchema) });

/** POST /v1/config/options */
export const optionCreateSchema = z.object({
  list_kind: z.enum(LIST_KINDS),
  value: z.string().min(1),
  display_name: z.string().optional(),
  minor_units: z.number().optional(),
});
export type OptionCreate = z.infer<typeof optionCreateSchema>;

/** Group a flat option list by kind — the shape every chip row and picker wants. */
export function groupOptions(rows: readonly OptionRow[]): Record<ListKind, OptionRow[]> {
  const out = {} as Record<ListKind, OptionRow[]>;
  for (const kind of LIST_KINDS) out[kind] = [];
  for (const row of rows) {
    const kind = row.list_kind as ListKind;
    if (out[kind]) out[kind].push(row);
  }
  for (const kind of LIST_KINDS) out[kind].sort((a, b) => a.sort_order - b.sort_order);
  return out;
}
